/* ==========================================================================
   NaturoPoints Explainer Page — np-points.js
   --------------------------------------------------------------------------
   Independent of nb-landing.js and nb2-landing.js: different selectors,
   different data attributes, no shared globals. All three can load on the
   same page without fighting over the same nodes.

   Dependency-free. Two jobs only:

     1. FAQ accordion (one open at a time, animated)
     2. "Open rewards" buttons — hands off to the Smile.io launcher when the
        app is on the page, otherwise the link's own href is followed

   Re-runs on Shopify theme editor section load.
   ========================================================================== */

(function () {
  'use strict';

  /* 1. FAQ accordion ------------------------------------------------------ */

  /* <details> has no height to animate between — it jumps from summary-only
     to full. So the click is intercepted and the height is driven here, which
     also makes closing a sibling a real transition rather than a snap.

     The markup still carries the native exclusive `name` attribute. That is
     the no-JavaScript path; once this takes over it drops the attribute,
     otherwise the browser would slam the siblings shut mid-animation. */

  var FAQ_DURATION = 260;
  var FAQ_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Height with the answer hidden: the summary plus the item's own borders,
     which offsetHeight counts and clientHeight does not. */
  function closedHeight(item, summary) {
    return summary.offsetHeight + item.offsetHeight - item.clientHeight;
  }

  function stop(item) {
    if (!item.npAnimation) return;
    item.npAnimation.cancel();
    item.npAnimation = null;
  }

  function slide(item, from, to, onDone) {
    item.style.overflow = 'hidden';

    var animation = item.animate(
      { height: [from + 'px', to + 'px'] },
      { duration: FAQ_DURATION, easing: FAQ_EASING }
    );
    item.npAnimation = animation;

    animation.onfinish = function () {
      item.npAnimation = null;
      item.style.overflow = '';
      if (onDone) onDone();
    };
  }

  function closeItem(item, summary) {
    // Read before stopping: cancelling drops the animated height.
    var from = item.offsetHeight;
    stop(item);

    if (!summary || reducedMotion()) {
      item.removeAttribute('data-np-closing');
      item.open = false;
      return;
    }

    // Drives the + back round now rather than when the slide lands.
    item.setAttribute('data-np-closing', '');

    slide(item, from, closedHeight(item, summary), function () {
      item.removeAttribute('data-np-closing');
      item.open = false;
    });
  }

  function openItem(item, list) {
    var others = list.querySelectorAll('details[open]');
    Array.prototype.forEach.call(others, function (other) {
      if (other !== item && !other.hasAttribute('data-np-closing')) {
        closeItem(other, other.querySelector('summary'));
      }
    });

    var from = item.offsetHeight;
    stop(item);
    item.removeAttribute('data-np-closing');
    item.open = true;

    if (reducedMotion()) return;
    slide(item, from, item.offsetHeight);
  }

  function initFaq(root) {
    var lists = root.querySelectorAll('[data-np-faq]');
    if (!lists.length || typeof Element.prototype.animate !== 'function') return;

    Array.prototype.forEach.call(lists, function (list) {
      if (list.dataset.npReady === 'true') return;
      list.dataset.npReady = 'true';

      var items = list.querySelectorAll('details');

      Array.prototype.forEach.call(items, function (item) {
        var summary = item.querySelector('summary');
        if (!summary) return;

        item.removeAttribute('name');

        summary.addEventListener('click', function (event) {
          event.preventDefault();

          if (item.open && !item.hasAttribute('data-np-closing')) {
            closeItem(item, summary);
          } else {
            openItem(item, list);
          }
        });
      });
    });
  }

  /* 2. Rewards panel launcher --------------------------------------------- */

  /* Smile.io is installed as an app embed, so its panel is available on every
     page. Where it is, "Open rewards" opens the panel in place; where it is
     not — an editor preview, the app being paused, a script blocker — the
     click is left alone and the link's href (an account page) is followed
     instead. The buttons are always real links, never bare buttons, so there
     is a working destination in every one of those cases. */

  function openRewardsPanel() {
    var ui = window.SmileUI;
    if (!ui || typeof ui.openPanel !== 'function') return false;

    try {
      ui.openPanel();
      return true;
    } catch (error) {
      return false;
    }
  }

  function initRewardsButtons(root) {
    var buttons = root.querySelectorAll('[data-np-rewards]');

    Array.prototype.forEach.call(buttons, function (button) {
      if (button.dataset.npReady === 'true') return;
      button.dataset.npReady = 'true';

      button.addEventListener('click', function (event) {
        if (openRewardsPanel()) event.preventDefault();
      });
    });
  }

  /* Boot ------------------------------------------------------------------ */

  function init(root) {
    var scope = root || document;
    initFaq(scope);
    initRewardsButtons(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
